<template>
  <messenger-view-layout
    :title="viewSettings.channel.name"
    :channel-type="WebWidgetChannelType.EMAIL"
    @back="$emit('back')"
  >
    <div class="view-content">
      <messenger-view-content-header
        class="view-content__header"
        :title="viewSettings.title"
        :description="viewSettings.description"
      />

      <div class="view-content__fields view-fields">
        <widget-form-line
          :label="$t('messengerWidget.nameField.label')"
          :required="fieldMap['name'].isRequired"
          :style="{ order: fieldMap['name'].order }"
        >
          <widget-form-field-text
            name="name"
            :placeholder="$t('messengerWidget.nameField.placeholder')"
            :tabindex="fieldMap['name'].order"
          />
        </widget-form-line>

        <widget-form-line
          :label="$t('messengerWidget.emailField.label')"
          :required="fieldMap['email'].isRequired"
          :style="{ order: fieldMap['email'].order }"
        >
          <widget-form-field-text
            name="email"
            :placeholder="$t('messengerWidget.emailField.placeholder')"
            :tabindex="fieldMap['email'].order"
          />
        </widget-form-line>

        <widget-form-line
          :label="$t('messengerWidget.messageField.label')"
          :required="fieldMap['message'].isRequired"
          :style="{ order: fieldMap['message'].order }"
        >
          <widget-form-field-textarea
            name="message"
            :placeholder="$t('messengerWidget.messageField.placeholder')"
            size="100px"
            :tabindex="fieldMap['message'].order"
          />
        </widget-form-line>
      </div>

      <widget-button
        v-bind="themeButtonProps"
        class="w100pr"
        :is-loading="isLoading"
        :loading-title="$t('common.sending')"
        @click="$emit('submit')"
      >
        <messenger-text-with-emoji :text="viewSettings.buttonText" />
      </widget-button>
    </div>

    <template v-slot:alert>
      <slot name="alert" />
    </template>
  </messenger-view-layout>
</template>

<script setup lang="ts">
import type { MessengerEmailViewSettings } from '@widgets/messenger/types'
import MessengerViewLayout from '@widgets/messenger/components/MessengerViewLayout.vue'
import WidgetFormLine from '@widgets/shared/components/WidgetFormLine.vue'
import WidgetFormFieldText from '@widgets/shared/components/fields/WidgetFormFieldText.vue'
import WidgetButton from '@widgets/shared/components/WidgetButton.vue'
import WidgetFormFieldTextarea from '@widgets/shared/components/fields/WidgetFormFieldTextarea.vue'
import { useFormFieldMap } from '@widgets/messenger/compositions/useFormFieldMap'
import MessengerViewContentHeader from '@widgets/messenger/components/MessengerViewContentHeader.vue'
import MessengerTextWithEmoji from '@widgets/messenger/components/MessengerTextWithEmoji.vue'
import { useWidgetTheme } from '@widgets/messenger/compositions/useWidgetTheme'
import {
  WebWidgetChannelType,
  type WebWidgetDetailsEmailFieldName,
} from '@/data/models/domain/messages/settings/webWidget/types'

const props = defineProps<{
  viewSettings: MessengerEmailViewSettings
  isLoading?: boolean
}>()

defineEmits<{
  (e: 'submit'): void
  (e: 'back'): void
}>()

const { themeButtonProps } = useWidgetTheme()

const { fieldMap } = useFormFieldMap<WebWidgetDetailsEmailFieldName>(() => props.viewSettings.formFields)
</script>

<style lang="scss" scoped>
.view-content {
  padding: 40px 24px 62px;

  &__header {
    margin-bottom: 24px;
  }

  &__fields {
    margin-bottom: 16px;
  }
}

.view-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
